goog.module('yext.entity.fields.ServiceAreaEdit');
goog.module.declareLegacyNamespace();

const yext = goog.require('yext');
const {SelectController} = goog.require('yext.ui.components.select.SelectController');

/**
 * An edit component for the Service Area Places portion of the compound address field
 * Taken from: yext/spruce/entitiesstorm/field-types/address/ServiceAreaEdit.jsx:10
 *
 * @author Hasan Atay (hatay@yext.com)
 */
class ServiceAreaEdit extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      serviceAreaPlaces: this.props.serviceAreaPlaces.map((serviceAreaPlace, index) => ({
        name: serviceAreaPlace.name,
        type: serviceAreaPlace.type,
        id: index,
      })),
      showServiceAreaPlaces: this.props.serviceAreaPlaces.length > 0,
      nextId: this.props.serviceAreaPlaces.length,
    };

    this.handleRemovePlace = this.handleRemovePlace.bind(this);
    this.handleAddNewPlace = this.handleAddNewPlace.bind(this);
    this.handlePlaceNameInputChange = this.handlePlaceNameInputChange.bind(this);
    this.handlePlaceTypeSelectChange = this.handlePlaceTypeSelectChange.bind(this);
    this.handlePlaceChange = this.handlePlaceChange.bind(this);
    this.getServiceAreaPlaceById = this.getServiceAreaPlaceById.bind(this);
    this.onStateChange = this.onStateChange.bind(this);
  }

  onStateChange() {
    const filteredServiceAreaPlaces = this.state.serviceAreaPlaces.filter(serviceAreaPlace =>
      serviceAreaPlace.name.length);
    this.props.onChange(filteredServiceAreaPlaces);
  }

  /**
   * Handles the submission event to add a place to serviceAreas.
   * Defaults to empty name and first type.
   *
   * @param {Object} _event
   */
  handleAddNewPlace(_event) {
    const newServiceAreaPlace = {
      name: '',
      type: 0,
      id: this.state.nextId};
    this.setState((state, _props) => ({
      nextId: state.nextId+1,
      serviceAreaPlaces: [...state.serviceAreaPlaces, newServiceAreaPlace],
    }));
  }

  /**
   * Handler for when a place is removed (remove by id).
   *
   * @param {int} id
   */
  handleRemovePlace(id) {
    this.setState((state, _props) => ({
      serviceAreaPlaces: state.serviceAreaPlaces.filter(
        serviceAreaPlace => serviceAreaPlace.id !== id,
      ),
    }), this.onStateChange);
  }

  /**
   * Updates the component's state when there is a change in place's name input.
   *
   * @param {String} name
   * @param {int} id
   */
  handlePlaceNameInputChange(name, id) {
    const serviceAreaPlace = this.getServiceAreaPlaceById(id);
    this.handlePlaceChange({
      name: name,
      type: serviceAreaPlace.type,
      id: id},
    id);
  }

  /**
   * Updates the component's state when there is a change in a place's type select.
   *
   * @param {String} type
   * @param {int} id
   */
  handlePlaceTypeSelectChange(type, id) {
    const serviceAreaPlace = this.getServiceAreaPlaceById(id);
    const typeRaw = Math.max(this.props.serviceAreaPlaceTypes.indexOf(type), 0);
    this.handlePlaceChange({
      name: serviceAreaPlace.name,
      type: typeRaw,
      id: id},
    id);
  }

  /**
   * Generically handles an existing service area place change
   * by setting the component's state with the updated value based on given id.
   *
   * Also triggers change upstream based on the updated value.
   *
   * @param {Object} newServiceAreaPlace
   * @param {int} id
   */
  handlePlaceChange(newServiceAreaPlace, id) {
    this.setState((state, _props) => ({
      serviceAreaPlaces: state.serviceAreaPlaces.map(serviceAreaPlace => {
        return serviceAreaPlace.id === id ? newServiceAreaPlace : serviceAreaPlace;
      }),
    }), this.onStateChange);
  }

  getServiceAreaPlaceById(id) {
    return this.state.serviceAreaPlaces.find(serviceAreaPlace => serviceAreaPlace.id === id);
  }

  renderPlace(id) {
    const {name, type} = this.getServiceAreaPlaceById(id);

    const typeSelectOptions = this.props.serviceAreaPlaceTypes.map(typeOption => ({
      value: typeOption,
      label: typeOption,
    }));
    const typeSelectConfig = {
      name: 'service-area-place-type-select-'+id,
      onSelect: value => this.handlePlaceTypeSelectChange(value, id),
      selectedOptions: this.props.serviceAreaPlaceTypes[type],
    };

    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    const removeButton = <div
      className="remove remove-item service-area-place-edit-row-input"
      onClick={_e => this.handleRemovePlace(id)}
    >
        [X]
    </div>;

    const typeSelector = <SelectController options={typeSelectOptions} config={typeSelectConfig}/>;

    const nameInput = <input
      name={'service-area-place-name-input-'+id}
      className="service-area-place-edit-row-input service-area-place-edit-row-name-input"
      tid="service-area-place-name"
      type="text"
      value={name}
      placeholder={yext.msg('Enter {0}', this.props.serviceAreaPlaceTypes[type])}
      onChange={e => this.handlePlaceNameInputChange(e.target.value, id)}/>;

    return (
      <li
        key={'service-area-places-edit-list-item-'+id}
        className="service-area-places-edit-list-item">
        <div className="service-area-place-edit-row">
          {removeButton}
          <span tid="service-area-place-type">
            {typeSelector}
          </span>
          {nameInput}
        </div>
      </li>
    );
  }

  handleShowServiceAreaPlacesChange(event) {
    this.setState({
      serviceAreaPlaces: event.target.checked
        ? this.props.serviceAreaPlaces.map((serviceAreaPlace, index) => ({
          name: serviceAreaPlace.name,
          type: serviceAreaPlace.type,
          id: index,
        })) : [],
      showServiceAreaPlaces: event.target.checked,
      nextId: event.target.checked ? this.props.serviceAreaPlaces.length : 0,
    }, this.onStateChange);
  }

  renderShouldShowServiceAreaPlaces() {
    return (
      <div className="service-area-places-provided">
        <label className="service-area-places-provided-label">
          <input
            className="service-area-places-provided-checkbox"
            tid="provide-service-area-checkbox"
            type="checkbox"
            checked={this.state.showServiceAreaPlaces}
            onChange={e => this.handleShowServiceAreaPlacesChange(e)}/>
          {yext.msg('We deliver or provide service at customer locations')}
        </label>
        {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
        <a
          className="address-service-area-places-edit-info info-icon contextual-help-right"
          tabIndex="0"
          role="button"
          ref={ref => this.ref = ref}/>
      </div>
    );
  }

  renderServiceAreaEdit() {
    return (
      <div className="service-area-places-edit">
        <br/>
        <div className="service-area-places-edit-title">
          <label className="service-area-places-edit-label">
            {yext.msg('Service Areas')}
          </label>
        </div>
        <br/>
        <div className="service-area-places-edit-container">
          <ul className="service-area-places-list">
            {this.state.serviceAreaPlaces.map(serviceAreaPlace =>
              this.renderPlace(serviceAreaPlace.id))}
          </ul>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <span
            className="service-area-places-edit-add link add-item add cobrand-second-secondary"
            tid="add-service-area-place"
            tabIndex="0"
            role="button"
            onClick={this.handleAddNewPlace}>
            {yext.msg('+ Add Service Area')}
          </span>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.renderShouldShowServiceAreaPlaces()}
        {this.state.showServiceAreaPlaces && this.renderServiceAreaEdit()}
      </div>
    );
  }
}

ServiceAreaEdit.propTypes = {
  serviceAreaPlaces: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
  })).isRequired,
  serviceAreaPlaceTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func,
};

ServiceAreaEdit.defaultProps = {
  onChange: () => { },
};

ServiceAreaEdit['propTypes'] = ServiceAreaEdit.propTypes;
ServiceAreaEdit['defaultProps'] = ServiceAreaEdit.defaultProps;

exports = ServiceAreaEdit;
